import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useMemo, useState } from "react";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { CheckInParams } from "lib/types/checkInParameters";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { CheckCard } from "render/ui/presentation/CheckCard";
import { Group } from "render/ui/presentation/Group";
import { BailView } from "render/views/BailView";
import styles from "./styles.module.sass";

type IdentityCheckViewProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

export function IdentityCheckView({
  goTo,
  onBack,
  patientId,
  visitId,
}: IdentityCheckViewProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const [identityConfirmed, setIdentityConfirmed] = useState(false);

  const isBodyScan = visit?.medicalExams?.includes(
    APITypesV1.MedicalExam.NekoBodyScan
  );

  const nextUrl = useMemo(() => {
    return isBodyScan
      ? paths.preferredName.url({ patientId, visitId })
      : paths.assignToRoom.url({ patientId, visitId });
  }, [isBodyScan, patientId, visitId]);

  const fullName =
    [patient?.name?.firstName, patient?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    patient?.name?.displayName ||
    "the member";

  const dateOfBirth = patient?.dateOfBirth?.toFormat("d MMMM yyyy");
  const physicalId = patient?.physicalIds?.[0];
  const physicalIdDisplay =
    physicalId != null
      ? typeof physicalId === "string"
        ? physicalId
        : physicalId.id
      : undefined;
  const countryCode = visit?.site.address.countryCode?.toUpperCase();
  const idLabel =
    countryCode === "SE"
      ? "Personnummer"
      : countryCode === "GB"
        ? "NHS number"
        : "Personal ID";

  if (!patient) {
    return <BailView title="Could not fetch member" />;
  }

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <>
            <div>
              <Typography variant="display-s">Confirm identity</Typography>
              <Typography variant="body-m" color="subtle">
                Verify the member&apos;s identity before continuing.
              </Typography>
            </div>
            <Group highlight={identityConfirmed ? "blue" : "none"}>
              <CheckCard
                aria-label="Confirm identity"
                isSelected={identityConfirmed}
                onChange={setIdentityConfirmed}
              >
                <div className={styles.cardBody}>
                  <Typography variant="eyebrow-m" color="subtle">
                    Member
                  </Typography>
                  <div className={styles.detailRow}>
                    <Typography variant="label-s" color="subtle">
                      Name
                    </Typography>
                    <Typography variant="body-m">{fullName}</Typography>
                  </div>
                  {dateOfBirth && (
                    <div className={styles.detailRow}>
                      <Typography variant="label-s" color="subtle">
                        Date of birth
                      </Typography>
                      <Typography variant="body-m">{dateOfBirth}</Typography>
                    </div>
                  )}
                  {physicalIdDisplay && (
                    <div className={styles.detailRow}>
                      <Typography variant="label-s" color="subtle">
                        {idLabel}
                      </Typography>
                      <Typography variant="body-m">
                        {physicalIdDisplay}
                      </Typography>
                    </div>
                  )}
                </div>
              </CheckCard>
            </Group>
          </>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={!identityConfirmed ? "disabled" : undefined}
                onPress={() => goTo(nextUrl)}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
